/**
 * V4-C — Create Your Business page.
 *
 * Signup for new Filipino SMB owners using Business Name + Mobile + 6-digit PIN.
 * No email, no Google, no OTP, no slug/URL/workspace questions. The workspace
 * slug is generated automatically from the business name.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/lib/auth";
import { Loader2, Eye, EyeOff, ShieldCheck, Store, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";

const PIN_LEN = 6;

export default function SignupPage() {
  const [businessName, setBusinessName] = useState("");
  const [mobile, setMobile] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const { ownerSignup, user, logout } = useAuth();
  const navigate = useNavigate();

  // If a previous session is active when landing on /signup, clear it so the
  // new business is created with a clean auth state (prevents the "previous
  // account shows first, refresh pushes to industry picker" issue).
  useEffect(() => {
    if (user) {
      logout().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Light formatting mask for the 10-digit local part. Server does the real
  // normalization — this is only for a friendlier typing experience.
  function fmtMobile(v) {
    const digits = String(v || "").replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return digits.slice(0, 3) + " " + digits.slice(3);
    return digits.slice(0, 3) + " " + digits.slice(3, 6) + " " + digits.slice(6);
  }
  function digitsOnly(v) { return String(v || "").replace(/\D/g, "").slice(0, PIN_LEN); }

  const canSubmit = businessName.trim().length >= 2
    && mobile.replace(/\D/g, "").length === 10
    && pin.length === PIN_LEN
    && confirmPin === pin
    && agree;

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await ownerSignup({
        business_name: businessName.trim(),
        mobile: "+63" + mobile.replace(/\D/g, ""),
        pin,
        confirm_pin: confirmPin,
      });
      toast.success(`Welcome, ${businessName.trim()} — your 7-day free trial has started`);
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // Keep local pin inputs as strings of digits only, respecting length cap.
  useEffect(() => { setPin((p) => digitsOnly(p)); }, []);

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo size={30} />
          </Link>
          <h1 className="mt-8 font-display text-3xl tracking-tighter">Create Your Business</h1>
          <p className="text-sm text-muted-foreground mt-1">Set up BizOne in less than a minute.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="owner-signup-form">
            <div>
              <Label htmlFor="business">Business Name</Label>
              <div className="mt-1.5 relative">
                <Store className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="business"
                  required
                  autoFocus
                  data-testid="signup-business-name"
                  placeholder="Aling Nena's Store"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="mobile">Mobile Number</Label>
              <div className="mt-1.5 flex items-stretch gap-0 rounded-md border border-border focus-within:ring-2 focus-within:ring-ring overflow-hidden">
                <div className="flex items-center gap-1 px-3 bg-muted text-sm font-medium">
                  <Phone className="h-3.5 w-3.5" /> +63
                </div>
                <Input
                  id="mobile"
                  required
                  type="tel"
                  inputMode="numeric"
                  data-testid="signup-mobile"
                  placeholder="9XX XXX XXXX"
                  value={fmtMobile(mobile)}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  className="border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">We'll use this to sign you in.</p>
            </div>
            <div>
              <Label htmlFor="pin">Create 6-Digit PIN</Label>
              <div className="mt-1.5 relative">
                <Input
                  id="pin"
                  required
                  type={showPin ? "text" : "password"}
                  inputMode="numeric"
                  data-testid="signup-pin"
                  pattern="\d{6}"
                  maxLength={PIN_LEN}
                  placeholder="••••••"
                  value={pin}
                  onChange={(e) => setPin(digitsOnly(e.target.value))}
                  className="pr-10 tracking-[0.5em] font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowPin((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground"
                  data-testid="signup-pin-toggle"
                  aria-label={showPin ? "Hide PIN" : "Show PIN"}
                >
                  {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirmPin">Confirm PIN</Label>
              <Input
                id="confirmPin"
                required
                type={showPin ? "text" : "password"}
                inputMode="numeric"
                data-testid="signup-pin-confirm"
                pattern="\d{6}"
                maxLength={PIN_LEN}
                placeholder="••••••"
                value={confirmPin}
                onChange={(e) => setConfirmPin(digitsOnly(e.target.value))}
                className="mt-1.5 tracking-[0.5em] font-mono"
              />
              {confirmPin.length === PIN_LEN && confirmPin !== pin && (
                <p className="mt-1 text-xs text-destructive">PINs don't match.</p>
              )}
            </div>
            <div className="flex items-start gap-2 pt-1">
              <Checkbox id="agree" checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="signup-agree" />
              <label htmlFor="agree" className="text-xs text-muted-foreground leading-5 cursor-pointer">
                I agree to the <a href="/terms" className="underline">Terms</a> & <a href="/privacy" className="underline">Privacy Policy</a>.
              </label>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={loading || !canSubmit}
              data-testid="signup-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create My Business"}
            </Button>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground justify-center">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> 7-day free trial · No payment required
            </div>
          </form>

          <p className="text-sm text-muted-foreground mt-5 text-center">
            Already have an account?{" "}
            <Link to="/login" data-testid="signup-switch-to-login" className="text-foreground font-medium hover:underline">Sign In</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:flex flex-col justify-between p-10 bg-secondary/40 border-l border-border relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div />
        <div className="relative">
          <div className="overline">Trusted by businesses like</div>
          <div className="mt-3 grid grid-cols-3 gap-4">
            {["Elm Coffee", "Torque Motors", "Aster Clinic", "Northwind", "Loop Studio", "Bright Bakery"].map(n => (
              <div key={n} className="rounded-md border border-border bg-background p-3 font-display text-sm tracking-tight">{n}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
